import React, { useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { aiApi } from "../../api/aiApi";
import { useAuth } from "../../hooks/useAuth";

interface Message {
  role: "user" | "assistant";
  content: string;
  sentAt: number;
}

const INITIAL_ASSISTANT =
  "Hello! I’m IntelliWarrior AI. Ask about your client roster, disputes, follow-ups, or strategy — I’ll use your business data when available.";

function formatTenantTitle(subdomain: string | undefined): string {
  if (!subdomain?.trim()) {
    return "Your business";
  }
  return subdomain
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function formatTime(ts: number) {
  return new Date(ts).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

const assistantMarkdownClass =
  "text-sm text-slate-200 [&_h1]:mb-2 [&_h1]:mt-0 [&_h1]:text-base [&_h1]:font-bold [&_h1]:text-white [&_h2]:mb-2 [&_h2]:mt-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-white [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold [&_p]:mb-2 [&_p]:last:mb-0 [&_ul]:mb-2 [&_ul]:ml-4 [&_ul]:list-disc [&_ul]:space-y-1 [&_ol]:mb-2 [&_ol]:ml-4 [&_ol]:list-decimal [&_ol]:space-y-1 [&_strong]:font-semibold [&_strong]:text-white [&_a]:text-purple-300 [&_a]:underline [&_code]:rounded [&_code]:bg-slate-950 [&_code]:px-1 [&_code]:text-xs [&_pre]:mb-2 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-slate-950 [&_pre]:p-2 [&_pre]:text-xs [&_blockquote]:border-l-2 [&_blockquote]:border-purple-500/60 [&_blockquote]:pl-3 [&_blockquote]:italic";

const ChatbotWidget: React.FC = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: INITIAL_ASSISTANT, sentAt: Date.now() },
  ]);
  const [typing, setTyping] = useState(false);

  const clearChat = () => {
    setMessages([{ role: "assistant", content: INITIAL_ASSISTANT, sentAt: Date.now() }]);
  };

  const send = async () => {
    const message = input.trim();
    if (!message) return;

    const historyForApi = messages.map((m) => ({ role: m.role, content: m.content }));
    const userTs = Date.now();
    setMessages((prev) => [...prev, { role: "user", content: message, sentAt: userTs }]);
    setInput("");
    setTyping(true);

    try {
      const res = await aiApi.chat({
        message,
        history: historyForApi,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: res.response, sentAt: Date.now() }]);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        console.log("ChatbotWidget chat API error:", error.response?.status, error.response?.data);
      } else {
        console.log("ChatbotWidget chat unknown error:", error);
      }
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I'm having trouble connecting. Please try again.",
          sentAt: Date.now(),
        },
      ]);
    } finally {
      setTyping(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-[max(1.5rem,env(safe-area-inset-right))] z-50 flex h-14 w-14 items-center justify-center rounded-full bg-purple-600 text-xl text-white shadow-lg shadow-purple-900/40 hover:bg-purple-500"
        aria-label="Open chatbot"
      >
        ✦
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex flex-col overflow-hidden border-0 bg-slate-900 shadow-2xl shadow-purple-900/30 lg:inset-auto lg:bottom-24 lg:right-6 lg:h-[min(500px,calc(100vh-7rem))] lg:w-[min(360px,calc(100vw-1.5rem))] lg:rounded-2xl lg:border lg:border-purple-700/40">
          <div className="flex shrink-0 items-start justify-between gap-2 border-b border-slate-800 bg-purple-900/40 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] lg:pt-3">
            <div className="min-w-0">
              <p className="text-sm text-purple-300">AI Assistant</p>
              <h3 className="truncate text-base font-semibold text-white">{formatTenantTitle(user?.subdomain)}</h3>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={clearChat}
                className="rounded-lg border border-slate-600/80 px-2.5 py-1 text-xs font-medium text-slate-200 hover:bg-slate-800"
              >
                Clear chat
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg border border-slate-600/80 p-2 text-slate-200 hover:bg-slate-800 lg:hidden"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="min-h-0 min-w-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden px-4 py-3">
            {messages.map((m, idx) => (
              <div
                key={`${m.role}-${idx}-${m.sentAt}`}
                className={`flex flex-col gap-0.5 ${m.role === "user" ? "items-end" : "items-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 ${
                    m.role === "user"
                      ? "bg-purple-600 text-white"
                      : "bg-slate-800 text-slate-200"
                  }`}
                >
                  {m.role === "assistant" ? (
                    <div className={assistantMarkdownClass}>
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap text-sm">{m.content}</p>
                  )}
                </div>
                <span className="px-1 text-[10px] text-slate-500">{formatTime(m.sentAt)}</span>
              </div>
            ))}
            {typing && (
              <div className="flex flex-col gap-0.5 items-start">
                <div className="flex max-w-[85%] items-center gap-1 rounded-lg bg-slate-800 px-4 py-3">
                  <span
                    className="h-2 w-2 rounded-full bg-purple-400 animate-bounce [animation-duration:0.55s]"
                    style={{ animationDelay: "0ms" }}
                  />
                  <span
                    className="h-2 w-2 rounded-full bg-purple-400 animate-bounce [animation-duration:0.55s]"
                    style={{ animationDelay: "120ms" }}
                  />
                  <span
                    className="h-2 w-2 rounded-full bg-purple-400 animate-bounce [animation-duration:0.55s]"
                    style={{ animationDelay: "240ms" }}
                  />
                </div>
                <span className="px-1 text-[10px] text-slate-500">&nbsp;</span>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-slate-800 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    void send();
                  }
                }}
                placeholder="Ask AI..."
                className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-200"
              />
              <button
                type="button"
                onClick={() => void send()}
                className="w-full shrink-0 rounded-lg bg-purple-600 px-3 py-2 text-sm font-semibold text-white hover:bg-purple-500 sm:w-auto"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
